import React, { useEffect } from "react"
import { createContext, useContext, useState } from "react"
import type { Zone } from "@/components/SelectZone"
import type { SearchFilters } from "@/model/search"
import type { Option } from "@/components/AutoSuggestComponent"

interface SearchContextType {
  // Estados de búsqueda
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  searchType: string
  setSearchType: (type: string) => void
  priceRange: number[]
  setPriceRange: (range: number[]) => void
  minPrice: number
  setMinPrice: (price: number) => void
  maxPrice: number
  setMaxPrice: (price: number) => void
  selectedZone: Zone | null
  setSelectedZone: React.Dispatch<React.SetStateAction<Zone | null>>
  features: Record<string, number>
  setFeatures: React.Dispatch<React.SetStateAction<Record<string, number>>>
  selectedServices: string[]
  setSelectedServices: React.Dispatch<React.SetStateAction<string[]>>
  keyword: string | null
  setKeyword: React.Dispatch<React.SetStateAction<string | null>>
  options: Option[]
  setOptions: React.Dispatch<React.SetStateAction<Option[]>>
  reset: boolean
  setReset: React.Dispatch<React.SetStateAction<boolean>>
  setData: React.Dispatch<React.SetStateAction<any>>

  // Métodos
  saveFilters: () => void
  clearFilters: () => void
  getSearchFilters: () => SearchFilters
}

const defaultContext: SearchContextType = {
  searchQuery: "",
  setSearchQuery: () => {},
  searchType: "alquilar",
  setSearchType: () => {},
  priceRange: [200000, 10000000],
  setPriceRange: () => {},
  minPrice: 200000,
  setMinPrice: () => {},
  maxPrice: 10000000,
  setMaxPrice: () => {},
  selectedZone: null,
  setSelectedZone: () => {},
  features: {},
  setFeatures: () => {},
  selectedServices: [],
  setSelectedServices: () => {},
  keyword: null,
  options: [],
  setOptions: () => {},
  reset: false,
  setReset: () => {},
  setKeyword: () => {},
  saveFilters: () => {},
  clearFilters: () => {},
  setData: () => {},
  getSearchFilters: () => ({}) as SearchFilters,
}

const SearchContext = createContext<SearchContextType>(defaultContext)

export const useSearchContext = () => useContext(SearchContext)

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estados de búsqueda
  const [searchQuery, setSearchQuery] = useState("")
  const [searchType, setSearchType] = useState("alquilar")
  const [priceRange, setPriceRange] = useState([200000, 10000000])
  const [minPrice, setMinPrice] = useState(200000)
  const [maxPrice, setMaxPrice] = useState(10000000)
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
  const [features, setFeatures] = useState<Record<string, number>>({})
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [keyword, setKeyword] = useState<string | null>(null)
  const [options, setOptions] = useState<Option[]>([])
  const [reset, setReset] = useState<boolean>(false)
  const [data, setData] = useState<any>()

  useEffect(() => {
    if (!data) return

    const minPrice = Math.min(
      ...data.getAllRealEstates.flatMap((property: any) =>
        property.offers
          .filter((offer: any) => offer.type === (searchType === "alquilar" ? "Renta" : "Venta"))
          .map((offer: any) => offer.price),
      ),
    )
    const maxPrice = Math.max(
      ...data.getAllRealEstates.flatMap((property: any) =>
        property.offers
          .filter((offer: any) => offer.type === (searchType === "alquilar" ? "Renta" : "Venta"))
          .map((offer: any) => offer.price),
      ),
    )
    setPriceRange([minPrice, maxPrice])
    setMaxPrice(maxPrice)
    setMinPrice(minPrice)
  }, [searchType])

  /*// Cargar filtros guardados al iniciar
  useEffect(() => {
    if (typeof window === "undefined") return

    try {
      const savedFilters = localStorage.getItem("searchFilters")
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters)

        setSearchQuery(parsedFilters.searchQuery || "")
        setSearchType(parsedFilters.searchType || "alquilar")
        setPriceRange(parsedFilters.priceRange || [200000, 10000000])
        setMinPrice(parsedFilters.minPrice || 200000)
        setMaxPrice(parsedFilters.maxPrice || 10000000)
        setSelectedZone(parsedFilters.selectedZone || null)
        setFeatures(parsedFilters.features || {})
        setSelectedServices(parsedFilters.selectedServices || [])
        setKeyword(parsedFilters.keyword || null)
        setOptions(parsedFilters.options || [])
        setReset(false)
      }
    } catch (error) {
      console.error("Error al cargar filtros guardados:", error)
    }
  }, [])*/

  // Mejorar la función saveFilters para manejar mejor la serialización
  const saveFilters = () => {
    /*if (typeof window === "undefined") return

    try {
      // Crear una versión serializable de los filtros
      const filtersToSave = {
        searchQuery,
        searchType,
        priceRange,
        minPrice,
        maxPrice,
        selectedZone,
        features,
        selectedServices,
        keyword,
        options,
      }

      // Guardar en localStorage
      localStorage.setItem("searchFilters", JSON.stringify(filtersToSave))
      console.log("Filtros guardados:", filtersToSave) // Para depuración
    } catch (error) {
      console.error("Error al guardar filtros:", error)
    }finally {
      setReset(false)
    }*/
  }

  // Limpiar filtros guardados
  const clearFilters = () => {
    if (typeof window === "undefined") return

    try {
      localStorage.removeItem("searchFilters")

      // Restablecer valores predeterminados
      setSearchQuery("")
      setSearchType("alquilar")
      setPriceRange([200000, 10000000])
      setMinPrice(200000)
      setMaxPrice(10000000)
      setSelectedZone(null)
      setFeatures({})
      setSelectedServices([])
      setKeyword(null)
      setOptions([])
    } catch (error) {
      console.error("Error al limpiar filtros:", error)
    }finally {
      setReset(true)
    }
  }

  // Reemplazar la función getSearchFilters por esta versión mejorada
  const getSearchFilters = (): SearchFilters => {
    console.log("Obteniendo filtros del contexto:", {
      searchQuery,
      searchType,
      priceRange,
      selectedZone,
      features,
      selectedServices,
      keyword,
      options,
    })

    // Asegurarse de que los valores son válidos antes de construir el objeto de filtros
    const validPriceRange = Array.isArray(priceRange) && priceRange.length === 2 ? priceRange : [minPrice, maxPrice]

    // Convertir features de objeto a array de objetos {type, quantity}
    const validFeatures =
      features && typeof features === "object"
        ? Object.keys(features)
          .filter((key) => features[key] !== 0)
          .map((key) => ({ type: key, quantity: features[key] }))
        : []

    // Asegurarse de que las coordenadas son válidas
    const validCoordinates =
      selectedZone && selectedZone.coordinates
        ? {
          lat: selectedZone.coordinates.lat,
          lon: selectedZone.coordinates.lon,
          radius: selectedZone.radius || 5,
        }
        : null

    // Construir y devolver el objeto de filtros
    const filters = {
      query: searchQuery && searchQuery.trim() !== "" ? searchQuery : null,
      minPrice: validPriceRange[0],
      maxPrice: validPriceRange[1],
      offerType: searchType === "alquilar" ? "Renta" : "Venta",
      availability: true,
      features: validFeatures.length > 0 ? validFeatures : null,
      coordinates: validCoordinates,
      services: selectedServices && Array.isArray(selectedServices) ? selectedServices : [],
      keywords: keyword ? [keyword] : [],
      page: 1,
      size: 10,
      type: null,
    }
    setReset(false)

    console.log("Filtros generados:", filters)
    return filters
  }

  const value = {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    priceRange,
    setPriceRange,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    selectedZone,
    setSelectedZone,
    features,
    setFeatures,
    selectedServices,
    setSelectedServices,
    keyword,
    setKeyword,
    saveFilters,
    clearFilters,
    getSearchFilters,
    options,
    setOptions,
    reset,
    setReset,
    setData
  }

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
}

